import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useGetModelPricings } from '@/graphql/queries/admin';
import { useUpdateModelPricing } from '@/graphql/mutations/admin';
import type { ModelPricing } from '@/modules/admin/types/admin.types';
import { ArrowLeft, Loader2, Save } from 'lucide-react';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function AnalyticsSettingsPage() {
  const { t } = useTranslation();
  const [localPricing, setLocalPricing] = useState<ModelPricing[]>([]);
  const [savingModel, setSavingModel] = useState<string | null>(null);

  const { data: pricingResult, isLoading: loading } = useGetModelPricings({
    paging: { pageNo: 1, pageSize: 100 },
  });
  const { updateModelPricingAsync: updatePricing } = useUpdateModelPricing();

  // Sync remote data to local state for editing
  useEffect(() => {
    if (pricingResult?.getModelPricingss?.items) {
      setLocalPricing(
        pricingResult.getModelPricingss.items.map((item) => ({
          id: item.ItemId,
          model: item.Model,
          promptCostPer1k: item.PromptCostPerk,
          completionCostPer1k: item.CompletionCostPerk,
          createdAt: item.CreatedDate,
          updatedAt: item.LastUpdatedDate,
        }))
      );
    }
  }, [pricingResult]);

  const handleSave = async (model: ModelPricing) => {
    setSavingModel(model.model);
    try {
      await updatePricing({
        ItemId: model.id,
        PromptCostPerk: Number(model.promptCostPer1k),
        CompletionCostPerk: Number(model.completionCostPer1k),
      });
    } finally {
      setSavingModel(null);
    }
  };

  const handleChange = (index: number, field: keyof ModelPricing, value: string) => {
    const updated = [...localPricing];
    updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
    setLocalPricing(updated);
  };

  return (
    // No horizontal padding on mobile so the card bleeds edge-to-edge;
    // the header row gets its own `px-4` to stay aligned with the rest
    // of the admin UI.
    <div className="flex-1 space-y-4 py-4 md:p-8 md:pt-6">
      <div className="flex items-center gap-2 md:gap-4  md:px-0">
        <Link to="/admin/analytics" className="shrink-0">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h2 className="text-xl md:text-3xl font-bold tracking-tight truncate">
          {t('ADMIN_ANALYTICSSETTINGS_TITLE')}
        </h2>
      </div>

      <div className="space-y-4">
        <div className="border bg-[#fff] border-secondary-100! border-x-0 md:border-x md:rounded-md">
          <div className="p-4 md:p-6">
            <h3 className="text-base md:text-lg font-medium">
              {t('ADMIN_ANALYTICSSETTINGS_MODELPRICING_TITLE')}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mb-4">
              {t('ADMIN_ANALYTICSSETTINGS_MODELPRICING_DESCRIPTION')}
            </p>

            {loading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              // Horizontal scroll on phones — the inputs need ~128px each
              // plus the model name and Save column, so it won't ever fit
              // on a 390px viewport.
              <div className="overflow-x-auto">
                <Table className="border-secondary-100!">
                  <TableHeader>
                    <TableRow className="border-secondary-100!">
                      <TableHead className="whitespace-nowrap">
                        {t('ADMIN_ANALYTICSSETTINGS_TABLE_HEADERS_MODELID')}
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        {t('ADMIN_ANALYTICSSETTINGS_TABLE_HEADERS_PROMPTCOST')}
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        {t('ADMIN_ANALYTICSSETTINGS_TABLE_HEADERS_COMPLETIONCOST')}
                      </TableHead>
                      <TableHead className="w-25 whitespace-nowrap">
                        {t('ADMIN_ANALYTICSSETTINGS_TABLE_HEADERS_ACTIONS')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {localPricing.map((item, index) => (
                      <TableRow key={item.model} className="border-secondary-200!">
                        <TableCell className="font-medium whitespace-nowrap">
                          {item.model}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.000001"
                            value={item.promptCostPer1k}
                            onChange={(e) => handleChange(index, 'promptCostPer1k', e.target.value)}
                            className="w-32"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.000001"
                            value={item.completionCostPer1k}
                            onChange={(e) =>
                              handleChange(index, 'completionCostPer1k', e.target.value)
                            }
                            className="w-32"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleSave(item)}
                            disabled={savingModel === item.model}
                          >
                            {savingModel === item.model ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {localPricing.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center h-24 text-muted-foreground">
                          {t('ADMIN_ANALYTICSSETTINGS_TABLE_EMPTY')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
